// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/procreateTranslate.js
//
// Procreate Brush.archive (NSKeyedArchiver "SilicaBrush") -> Voxemera brush.
//
// Mapping table. "Doc" means the behaviour is described in the Procreate
// Handbook; "Inferred" means the stored scale was inferred from values seen
// in real brushes (see BRUSH_IMPORT_COMPATIBILITY.md) and is approximate.
//
//   Shape.png                white = paint (Doc). shapeInverted flips it.
//   Grain.png                white = paint passes, black blocks (Doc).
//   plotSpacing              spacing as a fraction of dab size (Inferred).
//                            Values under MIN_SPACING are raised and the
//                            per-dab flow is compensated so the build-up
//                            per unit length is preserved.
//   plotJitter               lateral jitter: value x PROCREATE_JITTER_SCALE of
//                            the dab size. Calibrated by matching Procreate's own
//                            QuickLook stroke previews (x0.5 made blobs; x0.05
//                            reproduces their ragged-edge strokes).
//   plotJitterLongitudinal   jitter along the stroke, same scale.
//   shapeRotation  -1..1     follow stroke angle, -100%..100% (Doc).
//   shapeScatter   0..2      random rotation per dab; 2 = a full turn
//                            (Inferred from the 0-2 range).
//   shapeRandomise           random start angle each stroke (Doc).
//   shapeAzimuth             follow Apple Pencil azimuth (Doc).
//   shapeCount     0..1      shapes per dab = round(value*16) (Inferred:
//                            1/16 seen for single-shape brushes).
//   shapeRoundness           dab y-scale (Doc).
//   textureMovement 0..1     0 = texturized (canvas-fixed), 1 = moving (Doc).
//   textureScale             grain scale relative to its image (Inferred).
//   grainDepth, grainDepthMinimum, textureInverted, textureBrightness,
//   textureContrast          grain response (Doc).
//   dynamicsPressure{Size,Opacity,Bleed} + *Curve
//                            amount and response curve (Doc). Curves are
//                            stored as "{x, y}" strings in any order.
//   dynamicsTilt{Size,Opacity} + dynamicsTiltAngle
//                            tilt response above the threshold angle (Doc).
//   dynamicsJitter{Size,Opacity,Hue,Saturation,Lightness,Darkness}
//                            per-dab jitter (Doc); *Stroke* = per stroke.
//   pencilTaper*             Apple Pencil taper lengths/amounts (Doc).
//   maxOpacity, dynamicsGlazedFlow
//                            stroke opacity cap and per-dab flow (Doc).
//   renderingRecursiveMixing blending (wet mix) rendering (Inferred).
import { bakeCurve, isIdentityCurve, parseProcreateCurve } from './curves.js';
import { scoreCompatibility } from './brushSchema.js';

export const PROCREATE_MIN_SPACING = 0.02;
export const PROCREATE_JITTER_SCALE = 0.05;

function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function nonDefault(value, defaultValue = 0, epsilon = 0.005) {
  return Math.abs(num(value, defaultValue) - defaultValue) > epsilon;
}

function curveFrom(root, key) {
  const points = parseProcreateCurve(root[key]);
  if (points.length < 2 || isIdentityCurve(points)) return [];
  return bakeCurve(points, 'procreate');
}

// Per-dab flow that gives the same build-up per unit length when dabs are
// spread out by `factor` (= newSpacing / oldSpacing).
export function compensateFlow(flow, factor) {
  const f = clamp(num(flow, 1), 0.0001, 1);
  if (factor <= 1.0001 || f >= 0.9999) return f;
  return clamp(1 - Math.pow(1 - f, factor), 0.005, 1);
}

export function translateProcreateBrush(root, context = {}) {
  const r = root && typeof root === 'object' ? root : {};
  const implemented = ['shape tip'];
  const approximated = [];
  const missing = [];
  const notes = [];

  const rawSpacing = Math.max(0, num(r.plotSpacing, 0.1));
  const spacing = Math.max(PROCREATE_MIN_SPACING, rawSpacing);
  const spacingFactor = rawSpacing > 0 ? spacing / rawSpacing : 1;
  const glazedFlow = num(r.dynamicsGlazedFlow, 1);
  const baseFlow = glazedFlow > 0.001 ? glazedFlow : 1;
  const flow = compensateFlow(baseFlow, spacingFactor);
  implemented.push('spacing');
  if (spacingFactor > 1.05) approximated.push(`spacing ${(rawSpacing * 100).toFixed(2)}% raised to ${(spacing * 100).toFixed(0)}% with flow compensation`);

  // Blending modes only mix colour when wet-mix dilution is above zero; with
  // dilution at 0 (common for inks) they deposit like an intense glaze.
  const recursiveMixing = r.renderingRecursiveMixing === true;
  const activeMixing = recursiveMixing && num(r.dynamicsMix, 0) > 0.005;
  const renderingMode = activeMixing ? 'wet-mix' : 'glaze';
  if (activeMixing) {
    missing.push({ label: 'blending rendering (wet colour mixing, paint load and pull)', major: true });
  } else if (recursiveMixing) {
    approximated.push('blending rendering with no dilution rendered as glaze');
    if (nonDefault(r.dynamicsPressureMix)) missing.push('pressure → wet mix');
  } else {
    implemented.push('glazed rendering (stroke opacity cap)');
    if (r.renderingMaxTransfer === true || r.renderingModulatedTransfer === true) approximated.push('glaze variant (uniform/intense/heavy) rendered as light glaze');
  }

  const sizePressureAmount = clamp(Math.abs(num(r.dynamicsPressureSize, 0)), 0, 1);
  const opacityPressureAmount = clamp(Math.abs(num(r.dynamicsPressureOpacity, 0)), 0, 1);
  const bleedPressureAmount = clamp(Math.abs(num(r.dynamicsPressureBleed, 0)), 0, 1);
  if (sizePressureAmount > 0.005) implemented.push('pressure → size (curve)');
  if (opacityPressureAmount > 0.005) implemented.push('pressure → opacity (curve)');
  if (bleedPressureAmount > 0.005) implemented.push('pressure → flow/bleed (curve)');

  const tiltAngle = clamp(num(r.dynamicsTiltAngle, 0), 0, Math.PI / 2);
  const tiltSize = clamp(num(r.dynamicsTiltSize, 0), -1, 1);
  const tiltOpacity = clamp(num(r.dynamicsTiltOpacity, 0), -1, 1);
  if (Math.abs(tiltSize) > 0.005 || Math.abs(tiltOpacity) > 0.005) approximated.push('tilt → size/opacity (graph threshold approximated)');
  if (nonDefault(r.dynamicsTiltGradation) || nonDefault(r.dynamicsTiltBleed)) missing.push('tilt gradation/bleed');
  if (nonDefault(r.dynamicsTiltCompression, 0) && num(r.dynamicsTiltCompression) < 0.99 && num(r.dynamicsTiltCompression) > 0.01) approximated.push('tilt size compression');

  const speedSize = clamp(num(r.dynamicsSpeedSize, 0), -1, 1);
  const speedOpacity = clamp(num(r.dynamicsSpeedOpacity, 0), -1, 1);
  if (Math.abs(speedSize) > 0.005 || Math.abs(speedOpacity) > 0.005) approximated.push('speed → size/opacity');

  const sizeJitter = clamp(num(r.dynamicsJitterSize, 0), 0, 1);
  const opacityJitter = clamp(num(r.dynamicsJitterOpacity, 0), 0, 1);
  if (sizeJitter > 0.005 || opacityJitter > 0.005) implemented.push('size/opacity jitter');

  const perDab = {
    hue: clamp(num(r.dynamicsJitterHue, 0), 0, 1),
    saturation: clamp(num(r.dynamicsJitterSaturation, 0), 0, 1),
    lightness: clamp(num(r.dynamicsJitterLightness, 0), 0, 1),
    darkness: clamp(num(r.dynamicsJitterDarkness, 0), 0, 1),
  };
  const perStroke = {
    hue: clamp(num(r.dynamicsJitterStrokeHue, 0), 0, 1),
    saturation: clamp(num(r.dynamicsJitterStrokeSaturation, 0), 0, 1),
    lightness: clamp(num(r.dynamicsJitterStrokeLightness, 0), 0, 1),
    darkness: clamp(num(r.dynamicsJitterStrokeDarkness, 0), 0, 1),
  };
  if (Object.values(perDab).some((v) => v > 0.005)) implemented.push('colour jitter per dab');
  if (Object.values(perStroke).some((v) => v > 0.005)) implemented.push('colour jitter per stroke');
  if (['dynamicsPressureHue', 'dynamicsPressureSaturation', 'dynamicsPressureBrightness', 'dynamicsPressureSecondaryColor', 'dynamicsTiltHue', 'dynamicsTiltSaturation', 'dynamicsTiltBrightness', 'dynamicsTiltSecondaryColor'].some((key) => nonDefault(r[key]))) {
    missing.push('colour pressure/tilt (hue, saturation, brightness, secondary colour)');
  }

  const follow = clamp(num(r.shapeRotation, 0), -1, 1);
  const rotationJitter = clamp(num(r.shapeScatter, 0), 0, 2) * Math.PI;
  if (Math.abs(follow) > 0.005) implemented.push('shape follows stroke');
  if (rotationJitter > 0.01) approximated.push('shape scatter (random rotation, scale inferred)');
  if (r.shapeAzimuth === true) implemented.push('shape follows pencil azimuth');

  const countValue = Math.max(1, Math.min(16, Math.round(num(r.shapeCount, 0) * 16) || 1));
  if (countValue > 1) approximated.push(`shape count ${countValue} (scale inferred)`);

  const plotJitter = clamp(num(r.plotJitter, 0), 0, 8);
  const plotJitterAlong = clamp(num(r.plotJitterLongitudinal, 0), 0, 8);
  if (plotJitter > 0.005 || plotJitterAlong > 0.005) approximated.push('stroke jitter (scale calibrated against Procreate previews)');
  if (nonDefault(r.plotSpacingJitter)) missing.push('spacing jitter');

  const taperStart = clamp(num(r.pencilTaperStartLength, 0), 0, 1);
  const taperEnd = clamp(num(r.pencilTaperEndLength, 0), 0, 1);
  const taperSize = clamp(num(r.pencilTaperSize, 0), 0, 1);
  const taperOpacity = clamp(num(r.pencilTaperOpacity, 0), 0, 1);
  if ((taperStart > 0.005 || taperEnd > 0.005) && (taperSize > 0.005 || taperOpacity > 0.005)) {
    approximated.push('pressure taper (length scale approximated)');
    if (nonDefault(r.pencilTaperShape)) approximated.push('taper shape');
  }
  if (nonDefault(r.plotSmoothing) || nonDefault(r.plotFFTSmoothingAmount) || nonDefault(r.plotMovingAverageStabilization)) {
    approximated.push('StreamLine/stabilisation uses Voxemera smoothing');
  }

  let texture = null;
  if (context.hasGrain) {
    const movement = clamp(num(r.textureMovement, 0), 0, 1);
    const depth = clamp(num(r.grainDepth, 1), 0, 1);
    texture = {
      hash: context.grainHash || 'pending',
      mode: movement >= 0.5 ? 'moving' : 'texturized',
      movement,
      scale: clamp(num(r.textureScale, 1), 0.02, 4),
      depth,
      depthMinimum: clamp(num(r.grainDepthMinimum, 0), 0, 1),
      invert: r.textureInverted === true,
      brightness: clamp(num(r.textureBrightness, 0), -1, 1),
      contrast: clamp(num(r.textureContrast, 0), -1, 4),
      rotation: clamp(num(r.textureRotation, 0), -2, 2) * Math.PI,
      blend: 'multiply',
      offsetJitter: r.textureOffsetJitter === true,
    };
    implemented.push(texture.mode === 'moving' ? 'moving grain' : 'texturized grain');
    approximated.push('grain scale/zoom relative to brush size');
    if (movement > 0.02 && movement < 0.98) approximated.push(`grain movement ${(movement * 100).toFixed(0)}% interpolated`);
    if (nonDefault(r.grainDepthJitter)) missing.push('grain depth jitter');
    const grainBlend = num(r.grainBlendMode, 1);
    if (grainBlend !== 1 && grainBlend !== 0) approximated.push(`grain blend mode ${grainBlend} rendered as multiply`);
  }

  const wetEdges = clamp(num(r.wetEdgesAmount, 0), 0, 1);
  const burntEdges = clamp(num(r.burntEdgesAmount, 0), 0, 1);
  if (wetEdges > 0.005) missing.push('wet edges');
  if (burntEdges > 0.005) missing.push('burnt edges');
  if (num(r.blendMode, 0) !== 0) missing.push('brush blend mode ' + num(r.blendMode, 0));
  if (nonDefault(r.dynamicsBlur) || nonDefault(r.dynamicsBlurJitter)) missing.push('dab blur');
  if (nonDefault(r.dynamicsFalloff)) missing.push('stroke falloff');
  if (r.shapeFlipXJitter === true || r.shapeFlipYJitter === true) implemented.push('shape flip jitter');
  if (context.dualBrush) missing.push({ label: 'dual brush (secondary shape and grain)', major: true });
  if (r.importedFromABR === true) notes.push('This Procreate brush was itself converted from a Photoshop ABR.');

  const shapeAngle = num(r.shapeAngle, 0);
  const angle = Math.abs(shapeAngle) > Math.PI * 2 + 0.001 ? (shapeAngle * Math.PI) / 180 : shapeAngle;

  return {
    name: String(r.name || '').trim(),
    author: String(r.authorName || '').trim(),
    settings: {
      spacing,
      tipScale: 1,
      angle,
      roundness: clamp(num(r.shapeRoundness, 1), 0.02, 1),
      opacity: clamp(num(r.maxOpacity, 1), 0.01, 1),
      flow,
      opacityMode: 'stroke',
      eraser: false,
      edgeMode: 'normal',
      tipPolarity: r.shapeInverted === true ? 'dark' : 'light',
    },
    dynamics: {
      size: {
        pressure: { amount: sizePressureAmount, curve: curveFrom(r, 'dynamicsPressureSizeCurve') },
        // Planner convention: positive = less with tilt, negative = more.
        // Procreate tilt makes dabs larger and more transparent.
        tilt: { amount: -Math.abs(tiltSize), angle: tiltAngle },
        speed: speedSize,
        jitter: sizeJitter,
        minimum: clamp(num(r.minSize, 0) / Math.max(0.0001, num(r.maxSize, 1)), 0, 1),
      },
      opacity: {
        pressure: { amount: opacityPressureAmount, curve: curveFrom(r, 'dynamicsPressureOpacityCurve') },
        tilt: { amount: tiltOpacity, angle: tiltAngle },
        speed: speedOpacity,
        jitter: opacityJitter,
        minimum: clamp(num(r.minOpacity, 0), 0, 1) < 0.999 ? clamp(num(r.minOpacity, 0), 0, 1) : 0,
      },
      flow: {
        pressure: { amount: bleedPressureAmount, curve: curveFrom(r, 'dynamicsPressureBleedCurve') },
      },
      rotation: {
        follow,
        jitter: rotationJitter,
        randomPerStroke: r.shapeRandomise === true,
        azimuth: r.shapeAzimuth === true,
        flipXJitter: r.shapeFlipXJitter === true,
        flipYJitter: r.shapeFlipYJitter === true,
      },
      scatter: { perpendicular: plotJitter * PROCREATE_JITTER_SCALE, along: plotJitterAlong * PROCREATE_JITTER_SCALE, pressure: false },
      count: { value: countValue, jitter: clamp(num(r.shapeCountJitter, 0), 0, 1) },
      taper: {
        start: taperSize > 0.005 || taperOpacity > 0.005 ? taperStart : 0,
        end: taperSize > 0.005 || taperOpacity > 0.005 ? taperEnd : 0,
        size: taperSize,
        opacity: taperOpacity,
        pressure: clamp(num(r.taperPressure, 0), 0, 1),
      },
      colour: { perDab, perStroke },
    },
    texture,
    rendering: { mode: renderingMode, wetEdges, burntEdges, blendMode: 'normal' },
    compatibility: scoreCompatibility({ implemented, approximated, missing, notes }),
  };
}
